import React, { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react';
import type { TabsProps } from '@mui/material';
import { Tabs as TabsMUI } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { countValidChildren, omitUndefinedProps } from '@/mui/helpers';

import { CUSTOM_ATTRIBUTE_EQUAL_WIDTH, CUSTOM_ATTRIBUTE_SIZE } from './Tabs.api';

type Props = {
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  equalWidth?: boolean;
};

interface CustomCSSProperties extends CSSProperties {
  '--internal-scroll-gradient'?: string;
  '--internal-children-count'?: number;
}

const Tabs = forwardRef<HTMLElement, TabsProps & Props>((props, ref) => {
  const theme = useTheme();
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [scrollGradient, setScrollGradient] = useState(theme.palette.background.default);

  useEffect(() => {
    const getRecursiveBackgroundColor = (element: HTMLElement | null): string => {
      if (!element) {
        return theme.palette.background.default;
      }

      const { backgroundColor } = window.getComputedStyle(element);

      if (backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
        return backgroundColor;
      }

      if (element.parentElement) {
        return getRecursiveBackgroundColor(element.parentElement);
      }

      return theme.palette.background.default;
    };

    setScrollGradient(getRecursiveBackgroundColor(tabsRef.current));
  }, [theme, tabsRef.current]);

  const childrenCount = countValidChildren(props.children);

  return (
    <TabsMUI
      {...omitUndefinedProps({
        ...props,
        variant: props.variant !== 'fullWidth' && childrenCount <= 1 ? 'standard' : props.variant,
        size: undefined,
        equalWidth: undefined,
        [CUSTOM_ATTRIBUTE_SIZE]: props.size,
        [CUSTOM_ATTRIBUTE_EQUAL_WIDTH]: props.equalWidth,
      })}
      style={
        {
          ...(props.style || {}),
          '--internal-scroll-gradient': scrollGradient,
          '--internal-children-count': props.equalWidth ? childrenCount : 0,
        } as CustomCSSProperties
      }
      ref={(node) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }

        tabsRef.current = node;
      }}
    />
  );
});

Tabs.displayName = 'Tabs';

export default Tabs;
